/**
 * Shared export payload prep for table XLSX and table/dashboard PPTX.
 * Reuses existing fetch helpers + builders only — no new Firestore reads.
 */

import {
  getReportingResultsForMonths,
  type ReportingAggregateResult,
  type ReportingFetchOptions,
  type ReportingScopeOverrides,
  type ReportingSubmission,
} from './dashboardData';
import { fetchEthicData } from './ethicData';
import { fetchMedErrorIncidentData } from './medErrorIncidentData';
import { fetchFallIncidentData } from './fallIncidentData';
import { fetchKpoaIncidentData } from './kpoaIncidentData';
import { fetchHrImportDashboardBundle } from './hrImportData';
import type { ExportWorkbookSheet } from './exportService';
import {
  cellToPptxString,
  estimatePptxSlideCount,
  pptxSlideBudgetDisclosure,
  workbookSheetsToPptxSections,
  type PptxKpi,
  type PptxMetaRow,
  type PptxSection,
} from './pptxExport';
import type { RoleScope } from './auth/roleResolver';
import type { RoleContext } from '../lib/roles';
import type { ReportingMonth } from '../lib/reportingMonth';
import { filterSubmissionsForUi } from '../lib/departmentUi';
import {
  computeNurseNotesKpiSummary,
  NURSE_NOTES_OFFICIAL_KPIS,
} from '../lib/nurseNotesKpiMetrics';
import { buildEthicExportData } from '../utils/ethicExportRows';
import {
  buildFallIncidentExportData,
  buildMedErrorIncidentExportData,
  reportingMonthToFiscalYear,
  XLSX_SHEET_FALL_INCIDENT,
  XLSX_SHEET_MED_ERROR_INCIDENT,
} from '../utils/incidentReportsExport';
import { buildKpoaExportData, filterKpoaRowsByDateRange } from '../utils/kpoaExportRows';
import { buildHrExportPdfData, buildHrExportSheets } from '../utils/hrImportExportRows';
import { buildNurseNotesExportWorkbook } from '../utils/nurseNotesExportRows';
import { buildChartCategoryExportData } from '../utils/chartCategoryExportRows';
import { getExportPeriodContext, type ExportPeriod } from '../utils/exportPeriod';

export const KPOA_EXPORT_FETCH_CAP = 1000;
export const FALL_INCIDENT_EXPORT_FETCH_CAP = 1000;

export const EXPORT_PAYLOAD_CATEGORY_IDS = [
  'nurse_notes',
  'ethicSurveys',
  'med_error',
  'siren_bundle',
  'needleandsharp',
  'med_error_incident',
  'fall_incident',
  'kpoa',
  'hr',
] as const;

export type CanonicalExportCategoryId = (typeof EXPORT_PAYLOAD_CATEGORY_IDS)[number];

const CHART_SUBMISSION_EXPORT_CATEGORY_IDS = new Set<CanonicalExportCategoryId>([
  'med_error',
  'siren_bundle',
  'needleandsharp',
]);

export type PrepareExportPayloadErrorCode =
  | 'hr-no-batch'
  | 'kpoa-empty'
  | 'permission'
  | 'generic'
  | 'unsupported-category';

export type PrepareExportPayloadResult =
  | { ok: false; error: string; code?: PrepareExportPayloadErrorCode }
  | {
      ok: true;
      categoryId: CanonicalExportCategoryId;
      title: string;
      filenameBase: string;
      footerLeft: string;
      metaRows: PptxMetaRow[];
      headers?: string[];
      rows?: string[][];
      sections?: PptxSection[];
      workbookSheets?: ExportWorkbookSheet[];
      singleSheetName?: string;
      /** Flattened PDF-friendly table when needed */
      pdfHeaders?: string[];
      pdfRows?: string[][];
      kpis?: PptxKpi[];
      capMeta?: {
        fetchCap: number;
        fetchedCount: number;
        exportedCount: number;
        truncated: boolean;
      };
      empty: boolean;
      slideBudgetHint?: string | null;
    };

export type PrepareExportPayloadArgs = {
  categoryId: string;
  period: ExportPeriod;
  reportingMonth: ReportingMonth;
  categoryLabel: string;
  periodLabel: string;
  scopeLabel: string;
  scopedExportDepartmentIds: string[];
  departmentIdToName: Map<string, string>;
  roleScope: RoleScope | null;
  roleContext: RoleContext | null;
  reportingScope?: ReportingScopeOverrides;
  reportingOptions?: ReportingFetchOptions;
  /** When true, attach dashboard KPI tiles where the category defines them. */
  includeDashboardKpis?: boolean;
};

function isCanonicalCategory(id: string): id is CanonicalExportCategoryId {
  return (EXPORT_PAYLOAD_CATEGORY_IDS as readonly string[]).includes(id);
}

type CapMeta = {
  fetchCap: number;
  fetchedCount: number;
  exportedCount: number;
  truncated: boolean;
};

function withCapMetaRows(metaRows: PptxMetaRow[], capMeta: CapMeta): PptxMetaRow[] {
  if (!capMeta.truncated) return metaRows;
  return [
    ...metaRows,
    [
      'หมายเหตุการจำกัดข้อมูล',
      `ดึงได้ ${capMeta.fetchedCount} จากสูงสุด ${capMeta.fetchCap} รายการ ส่งออก ${capMeta.exportedCount} รายการ`,
    ],
  ];
}

function nurseNotesKpis(submissions: ReportingSubmission[]): PptxKpi[] {
  const summary = computeNurseNotesKpiSummary(submissions);
  return NURSE_NOTES_OFFICIAL_KPIS.map((kpi) => {
    const card = summary.cards.find((c) => c.id === kpi.id);
    return {
      id: kpi.id,
      label: kpi.shortLabel,
      value: card?.displayValue === '-' ? 'ไม่มีข้อมูล' : (card?.displayValue ?? 'ไม่มีข้อมูล'),
    };
  });
}

function detailReportingOptions(
  options: ReportingFetchOptions | undefined,
): ReportingFetchOptions | undefined {
  if (!options) return { fetchMode: 'detail' };
  if (options.fetchMode === 'summary') {
    return { ...options, fetchMode: 'detail' };
  }
  return { ...options, fetchMode: options.fetchMode ?? 'detail' };
}

/**
 * Pin export fetches to ExportHub's department list so HWN/own-ward scope
 * cannot widen to LOF or hospital-wide when reportingScope is missing/stale.
 */
export function resolveExportFetchScope(
  reportingScope: ReportingScopeOverrides | undefined,
  scopedExportDepartmentIds: string[],
): ReportingScopeOverrides | undefined {
  const scoped = scopedExportDepartmentIds.filter((id) => typeof id === 'string' && id.trim());
  if (scoped.length === 0) return reportingScope;
  if (scoped.includes('*')) {
    return { ...reportingScope, departmentIds: ['*'] };
  }
  return { ...reportingScope, departmentIds: scoped };
}

export async function prepareExportPayload(
  args: PrepareExportPayloadArgs,
): Promise<PrepareExportPayloadResult> {
  if (!isCanonicalCategory(args.categoryId)) {
    return {
      ok: false,
      error: 'หมวดหมู่นี้ยังไม่รองรับการส่งออก',
      code: 'unsupported-category',
    };
  }

  const periodContext = getExportPeriodContext(args.period, args.reportingMonth);
  const includeKpis = !!args.includeDashboardKpis;

  try {
    switch (args.categoryId) {
      case 'ethicSurveys': {
        const ids = args.scopedExportDepartmentIds.includes('*')
          ? ['*']
          : args.scopedExportDepartmentIds;
        const { startDate, endDate } = periodContext;
        const fetched = await fetchEthicData({
          departmentIds: ids,
          startDate,
          endDate,
        });
        const { headers, rows } = buildEthicExportData(fetched, args.departmentIdToName);
        const empty = rows.length === 0;
        const dataRows = empty ? [PLACEHOLDER(headers.length)] : rows;
        const metaRows: PptxMetaRow[] = [
          ['หน่วยงาน', args.scopeLabel],
          [
            'ช่วงข้อมูล',
            `${startDate.toLocaleDateString('th-TH')} - ${endDate.toLocaleDateString('th-TH')}`,
          ],
          ['จำนวนรายการ', String(rows.length)],
        ];
        const kpis: PptxKpi[] | undefined = includeKpis
          ? [
              { id: 'rowCount', label: 'จำนวนรายการ', value: String(rows.length) },
              { id: 'scope', label: 'หน่วยงาน', value: args.scopeLabel },
              {
                id: 'range',
                label: 'ช่วงข้อมูล',
                value: `${startDate.toLocaleDateString('th-TH')} - ${endDate.toLocaleDateString('th-TH')}`,
              },
            ]
          : undefined;
        const filenameBase = `ethic-${args.reportingMonth}`;
        const projected = estimatePptxSlideCount({
          headers,
          rows: dataRows,
          includeCover: true,
          includeKpis,
        });
        return {
          ok: true,
          categoryId: args.categoryId,
          title: args.categoryLabel,
          filenameBase,
          footerLeft: `${args.categoryLabel} · ${args.periodLabel}`,
          metaRows,
          headers,
          rows: dataRows,
          singleSheetName: 'แบบสำรวจจริยธรรม',
          pdfHeaders: headers,
          pdfRows: dataRows,
          kpis,
          empty,
          slideBudgetHint: pptxSlideBudgetDisclosure(projected),
        };
      }

      case 'med_error_incident': {
        const fiscalYear = reportingMonthToFiscalYear(args.reportingMonth);
        const fetched = await fetchMedErrorIncidentData({
          departmentIds: ['*'],
          fiscalYear,
        });
        const { headers, rows } = buildMedErrorIncidentExportData(fetched, args.departmentIdToName);
        const empty = rows.length === 0;
        const dataRows = empty ? [PLACEHOLDER(headers.length)] : rows;
        const metaRows: PptxMetaRow[] = [
          ['หน่วยงาน', 'ทุกหน่วยงาน'],
          ['ปีงบประมาณ', String(fiscalYear)],
          ['จำนวนรายการ', String(rows.length)],
        ];
        const kpis: PptxKpi[] | undefined = includeKpis
          ? [
              { id: 'count', label: 'จำนวนรายการ', value: String(rows.length) },
              { id: 'fy', label: 'ปีงบประมาณ', value: String(fiscalYear) },
            ]
          : undefined;
        return {
          ok: true,
          categoryId: args.categoryId,
          title: args.categoryLabel,
          filenameBase: `med-error-incident-${fiscalYear}`,
          footerLeft: `${args.categoryLabel} · ปีงบ ${fiscalYear}`,
          metaRows,
          headers,
          rows: dataRows,
          singleSheetName: XLSX_SHEET_MED_ERROR_INCIDENT,
          pdfHeaders: headers,
          pdfRows: dataRows,
          kpis,
          empty,
          slideBudgetHint: pptxSlideBudgetDisclosure(
            estimatePptxSlideCount({ headers, rows: dataRows, includeCover: true, includeKpis }),
          ),
        };
      }

      case 'fall_incident': {
        const fiscalYear = reportingMonthToFiscalYear(args.reportingMonth);
        const fetched = await fetchFallIncidentData({
          limit: FALL_INCIDENT_EXPORT_FETCH_CAP,
          fiscalYear,
        });
        const { headers, rows } = buildFallIncidentExportData(fetched, args.departmentIdToName);
        const empty = rows.length === 0;
        const dataRows = empty ? [PLACEHOLDER(headers.length)] : rows;
        const capMeta = {
          fetchCap: FALL_INCIDENT_EXPORT_FETCH_CAP,
          fetchedCount: fetched.length,
          exportedCount: rows.length,
          truncated: fetched.length >= FALL_INCIDENT_EXPORT_FETCH_CAP,
        };
        const metaRows = withCapMetaRows(
          [
            ['ขอบเขต', 'ทุกหน่วยงาน (ข้อมูลนำเข้า)'],
            ['ปีงบประมาณ', String(fiscalYear)],
            ['จำนวนรายการ', String(rows.length)],
          ],
          capMeta,
        );
        const kpis: PptxKpi[] | undefined = includeKpis
          ? [
              { id: 'count', label: 'จำนวนรายการ', value: String(rows.length) },
              { id: 'fy', label: 'ปีงบประมาณ', value: String(fiscalYear) },
            ]
          : undefined;
        return {
          ok: true,
          categoryId: args.categoryId,
          title: args.categoryLabel,
          filenameBase: `fall-incident-${fiscalYear}`,
          footerLeft: `${args.categoryLabel} · ปีงบ ${fiscalYear}`,
          metaRows,
          headers,
          rows: dataRows,
          singleSheetName: XLSX_SHEET_FALL_INCIDENT,
          pdfHeaders: headers,
          pdfRows: dataRows,
          kpis,
          capMeta,
          empty,
          slideBudgetHint: pptxSlideBudgetDisclosure(
            estimatePptxSlideCount({ headers, rows: dataRows, includeCover: true, includeKpis }),
          ),
        };
      }

      case 'kpoa': {
        const ids = args.scopedExportDepartmentIds.includes('*')
          ? ['*']
          : args.scopedExportDepartmentIds;
        const { startDate, endDate, fiscalYear } = periodContext;
        const fetched = await fetchKpoaIncidentData({
          limit: KPOA_EXPORT_FETCH_CAP,
          departmentIds: ids,
          preservePermissionErrors: true,
        });
        const filteredRows = filterKpoaRowsByDateRange(fetched, startDate, endDate);
        const { headers, rows } = buildKpoaExportData(filteredRows, args.departmentIdToName);
        if (rows.length === 0) {
          return {
            ok: false,
            error: 'ไม่พบข้อมูล คปอ. ในช่วงเวลาหรือขอบเขตหน่วยงานที่เลือก',
            code: 'kpoa-empty',
          };
        }
        const capMeta = {
          fetchCap: KPOA_EXPORT_FETCH_CAP,
          fetchedCount: fetched.length,
          exportedCount: rows.length,
          truncated: fetched.length >= KPOA_EXPORT_FETCH_CAP,
        };
        const metaRows = withCapMetaRows(
          [
            ['หมวดหมู่', 'คปอ.'],
            ['หน่วยงาน', args.scopeLabel],
            ['ช่วงเวลา', args.periodLabel],
            ['ปีงบประมาณ', String(fiscalYear)],
            [
              'ช่วงข้อมูล',
              `${startDate.toLocaleDateString('th-TH')} - ${endDate.toLocaleDateString('th-TH')}`,
            ],
            [
              'จำนวนรายการ',
              `${rows.length} จากข้อมูลที่ดึงสูงสุด ${KPOA_EXPORT_FETCH_CAP} รายการ`,
            ],
            ['หมายเหตุ', 'ข้อมูล คปอ. ใช้ชุดข้อมูลล่าสุดที่ระบบอ่านได้ แล้วกรองตามช่วงเวลาที่เลือก'],
            ['คำอธิบายค่าว่าง', '— หมายถึงไม่มีข้อมูลหรือไม่เกี่ยวข้องกับรายการนั้น'],
          ],
          capMeta,
        );
        const kpis: PptxKpi[] | undefined = includeKpis
          ? [
              { id: 'count', label: 'จำนวนรายการ', value: String(rows.length) },
              { id: 'fy', label: 'ปีงบประมาณ', value: String(fiscalYear) },
            ]
          : undefined;
        return {
          ok: true,
          categoryId: args.categoryId,
          title: args.categoryLabel,
          filenameBase: `kpoa-${args.reportingMonth}`,
          footerLeft: `${args.categoryLabel} · ${args.periodLabel}`,
          metaRows,
          headers,
          rows,
          singleSheetName: 'KPOA',
          pdfHeaders: headers,
          pdfRows: rows,
          kpis,
          capMeta,
          empty: false,
          slideBudgetHint: pptxSlideBudgetDisclosure(
            estimatePptxSlideCount({ headers, rows, includeCover: true, includeKpis }),
          ),
        };
      }

      case 'hr': {
        const { fiscalYear } = periodContext;
        const bundle = await fetchHrImportDashboardBundle();
        if (!bundle.activeBatchId) {
          return {
            ok: false,
            error: 'ยังไม่มีชุดข้อมูลทรัพยากรบุคคลที่พร้อมส่งออก',
            code: 'hr-no-batch',
          };
        }
        const workbookSheets = buildHrExportSheets(bundle, fiscalYear);
        const { sections, legendMeta } = workbookSheetsToPptxSections(workbookSheets);
        const { headers: pdfHeaders, rows: pdfRows } = buildHrExportPdfData(bundle, fiscalYear);
        const metaRows: PptxMetaRow[] = [
          ...legendMeta.slice(0, 8),
          ['หมวดหมู่', 'ทรัพยากรบุคคล'],
          ['ปีงบประมาณ', String(fiscalYear)],
        ];
        const kpis: PptxKpi[] | undefined = includeKpis
          ? [
              { id: 'batch', label: 'ชุดข้อมูล', value: String(bundle.activeBatchId) },
              { id: 'fy', label: 'ปีงบประมาณ', value: String(fiscalYear) },
              {
                id: 'annual',
                label: 'ตัวชี้วัดรายปี',
                value: String(bundle.meta?.writtenRecordCount?.annualMetrics ?? '—'),
              },
            ]
          : undefined;
        const empty = sections.every((s) => s.rows.length === 0);
        return {
          ok: true,
          categoryId: args.categoryId,
          title: args.categoryLabel,
          filenameBase: `hr-${fiscalYear}`,
          footerLeft: `${args.categoryLabel} · ปีงบ ${fiscalYear}`,
          metaRows,
          sections,
          workbookSheets,
          pdfHeaders,
          pdfRows,
          kpis,
          empty,
          slideBudgetHint: pptxSlideBudgetDisclosure(
            estimatePptxSlideCount({ sections, includeCover: true, includeKpis }),
          ),
        };
      }

      case 'med_error':
      case 'siren_bundle':
      case 'needleandsharp':
      case 'nurse_notes': {
        const months = periodContext.months;
        const fetchOptions = detailReportingOptions(args.reportingOptions);
        const exportFetchScope = resolveExportFetchScope(
          args.reportingScope,
          args.scopedExportDepartmentIds,
        );
        // Settle per month (same as dashboard year load) so one denied month
        // does not abort the whole XLSX/PPTX/PDF export for HWN-scoped users.
        const monthEntries = await getReportingResultsForMonths(months, {
          category: args.categoryId,
          roleScope: args.roleScope ?? undefined,
          roleContext: args.roleContext ?? undefined,
          overrides: exportFetchScope,
          options: fetchOptions,
        });
        const yearlyResults: Array<{
          reportingMonth: ReportingMonth;
          result: ReportingAggregateResult;
        }> = monthEntries.map((entry) => ({
          reportingMonth: entry.reportingMonth,
          result: { ...entry.result, months },
        }));

        const pooled = filterSubmissionsForUi(
          yearlyResults.flatMap((entry) => entry.result.submissions ?? []),
        );
        const empty = pooled.length === 0;
        const safeCategoryLabel = args.categoryLabel.replace(/\s/g, '-');
        const filenameBase = `table-${safeCategoryLabel}-${args.periodLabel}-${periodContext.fiscalYear}`;
        const footerLeft = `${args.categoryLabel} · ${args.periodLabel}`;

        if (CHART_SUBMISSION_EXPORT_CATEGORY_IDS.has(args.categoryId)) {
          const { headers, rows } = buildChartCategoryExportData(
            yearlyResults,
            months,
            args.categoryId,
          );
          const dataRows = empty ? [PLACEHOLDER(headers.length)] : rows;
          const metaRows: PptxMetaRow[] = [
            ['หน่วยงาน', args.scopeLabel],
            ['ช่วงเวลา', args.periodLabel],
            ['ปีงบประมาณ', String(periodContext.fiscalYear)],
            ['หมวดหมู่', args.categoryLabel],
            ['จำนวนรายการ', String(pooled.length)],
          ];
          const kpis: PptxKpi[] | undefined = includeKpis
            ? [
                { id: 'rowCount', label: 'จำนวนรายการ', value: String(pooled.length) },
                { id: 'scope', label: 'หน่วยงาน', value: args.scopeLabel },
                { id: 'period', label: 'ช่วงเวลา', value: args.periodLabel },
              ]
            : undefined;
          return {
            ok: true,
            categoryId: args.categoryId,
            title: args.categoryLabel,
            filenameBase,
            footerLeft,
            metaRows,
            headers,
            rows: dataRows,
            singleSheetName: args.categoryLabel.slice(0, 31) || 'ข้อมูล',
            pdfHeaders: headers,
            pdfRows: dataRows,
            kpis,
            empty,
            slideBudgetHint: pptxSlideBudgetDisclosure(
              estimatePptxSlideCount({
                headers,
                rows: dataRows,
                includeCover: true,
                includeKpis,
              }),
            ),
          };
        }

        const workbookSheets = buildNurseNotesExportWorkbook({
          period: args.period,
          fiscalYear: periodContext.fiscalYear,
          fiscalQuarter: periodContext.fiscalQuarter,
          months,
          monthlyResults: yearlyResults,
          deptIdToName: args.departmentIdToName,
          categoryLabel: args.categoryLabel,
          scopeLabel: args.scopeLabel,
        });
        const { sections, legendMeta } = workbookSheetsToPptxSections(workbookSheets);
        const metaRows: PptxMetaRow[] = [
          ...legendMeta.map(([k, v]) => [k, cellToPptxString(v)] as PptxMetaRow),
        ];
        if (!metaRows.length) {
          metaRows.push(
            ['หน่วยงาน', args.scopeLabel],
            ['ช่วงเวลา', args.periodLabel],
            ['ปีงบประมาณ', String(periodContext.fiscalYear)],
            ['หมวดหมู่', args.categoryLabel],
          );
        }
        const kpis = includeKpis ? nurseNotesKpis(pooled) : undefined;
        return {
          ok: true,
          categoryId: args.categoryId,
          title: args.categoryLabel,
          filenameBase,
          footerLeft,
          metaRows,
          sections,
          workbookSheets,
          kpis,
          empty,
          slideBudgetHint: pptxSlideBudgetDisclosure(
            estimatePptxSlideCount({ sections, includeCover: true, includeKpis }),
          ),
        };
      }
    }
  } catch (error) {
    const code =
      typeof (error as { code?: unknown })?.code === 'string'
        ? (error as { code: string }).code
        : '';
    const message = error instanceof Error ? error.message : '';
    const text = `${code} ${message}`.toLowerCase();
    if (text.includes('permission-denied') || text.includes('missing or insufficient permissions')) {
      return {
        ok: false,
        error: 'ไม่มีสิทธิ์อ่านข้อมูลสำหรับการส่งออกนี้ กรุณาตรวจสอบขอบเขตหน่วยงานหรือแจ้งผู้ดูแลระบบ',
        code: 'permission',
      };
    }
    return {
      ok: false,
      error: message || 'ส่งออกไม่สำเร็จ',
      code: 'generic',
    };
  }
}

function PLACEHOLDER(colCount: number): string[] {
  if (colCount <= 1) return ['ไม่มีข้อมูลในช่วงที่เลือก'];
  return ['ไม่มีข้อมูลในช่วงที่เลือก', ...Array.from({ length: colCount - 1 }, () => '—')];
}
